// Edición de un SKU del catálogo (PATCH): el tipo de ítem, el modo de rastreo y el SKU no se editan.
import { useId, useState } from 'react';
import { Controller, useForm, type Control, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import { useIdempotentAttempt } from '@/hooks/useIdempotentAttempt';
import { useCatalogProducts, useUpdateProduct } from '../../api/useCatalogWrites';
import { useSkuPrices } from '../../api/useInventorySku';
import {
    editFormOf,
    editSchemaFor,
    integratedProductEditSchema,
    itemTypeOf,
    TRACKING_MODE_OPTIONS,
    toUpdateProductRequest,
    type EditForm,
} from '../../lib/catalogForms';
import { applyWriteError } from '../../lib/inventoryWriteErrors';
import {
    IntegrationLockNotice,
    useIntegrationLock,
} from '../../../integration/components/IntegrationLock';
import {
    isIntegrationFreeKind,
    toIntegratedProductPatch,
} from '../../../integration/lib/integrationLock';
import { priceOrDash } from '../detail/PriceHistoryModal';
import { RegisterPriceForm } from '../detail/PriceTab';
import { EditorDrawer, EditorFooter, FinancierChecks, FormSection } from './FormBits';
import {
    KitComponentsField,
    componentOptions,
    type KitComponentValues,
} from './KitComponentsField';
import {
    IntegratedProductFields,
    ProductFields,
    SaleSwitches,
    TextController,
    type ProductFieldValues,
} from './ProductFields';

export function trackingLabel(mode: string | undefined): string {
    return TRACKING_MODE_OPTIONS.find((o) => o.value === mode)?.label ?? '—';
}

const KEEP_HINT = 'Un campo de texto que dejes vacío conserva su valor actual.';

function KitFields({
    form,
    product,
    retailerId,
}: {
    form: UseFormReturn<EditForm>;
    product: CatalogProduct;
    retailerId: string | null;
}) {
    const products = useCatalogProducts(retailerId, true);
    const shared = form.control as unknown as Control<ProductFieldValues>;
    const current = (product.components ?? []).map((c) => c.sku);
    return (
        <>
            <FormSection title="Kit">
                <TextController control={shared} name="description" label="Nombre del kit" />
                <div className="grid grid-cols-2 gap-3">
                    <TextController control={shared} name="externalId" label="ID externo" />
                    <TextController control={shared} name="imageUrl" label="URL de imagen" />
                </div>
            </FormSection>
            <FormSection title="Componentes">
                <KitComponentsField
                    control={form.control as unknown as Control<KitComponentValues>}
                    options={componentOptions(products.data ?? [], product.sku, current)}
                    isLoading={products.isPending}
                    error={form.formState.errors.components?.message}
                />
                <p className="text-[10.5px] text-muted-foreground">
                    Cambiar los componentes no toca los apartados vivos.
                </p>
            </FormSection>
            <FormSection title="Venta">
                <SaleSwitches control={shared} />
                <Controller
                    control={form.control}
                    name="eligibleFinanciers"
                    render={({ field }) => (
                        <FinancierChecks value={field.value} onChange={field.onChange} />
                    )}
                />
            </FormSection>
        </>
    );
}

function ServiceFields({ control }: { control: Control<ProductFieldValues> }) {
    return (
        <>
            <FormSection title="Servicio">
                <TextController
                    control={control}
                    name="description"
                    label="Nombre del servicio"
                    required
                />
                <TextController control={control} name="externalId" label="ID externo" />
            </FormSection>
            <FormSection title="Venta">
                <SaleSwitches control={control} />
            </FormSection>
        </>
    );
}

/** Precio vigente y registro de uno nuevo; es su propio formulario porque cada precio es un registro. */
function PriceSection({ sku, locked }: { sku: string; locked: boolean }) {
    const prices = useSkuPrices(sku, true);
    const current = prices.data?.items.at(0);
    return (
        <FormSection title="Precio">
            <p className="text-[12px]">
                Vigente:{' '}
                <strong className="tabular-nums">{priceOrDash(current?.basePriceCents)}</strong>
            </p>
            {locked ? (
                <IntegrationLockNotice message="lockedPrice" />
            ) : (
                <RegisterPriceForm sku={sku} />
            )}
        </FormSection>
    );
}

function ItemEditor({
    product,
    retailerId,
    onClose,
}: {
    product: CatalogProduct;
    retailerId: string | null;
    onClose: () => void;
}) {
    const formId = useId();
    const kind = itemTypeOf(product);
    const integrated = useIntegrationLock().locked && !isIntegrationFreeKind(kind);
    const update = useUpdateProduct(product.sku);
    const attempt = useIdempotentAttempt(`update:${product.sku}`);
    const [error, setError] = useState<string | null>(null);
    const form = useForm<EditForm>({
        resolver: zodResolver(integrated ? integratedProductEditSchema : editSchemaFor(kind)),
        defaultValues: editFormOf(product),
    });
    const shared = form.control as unknown as Control<ProductFieldValues>;
    return (
        <EditorDrawer
            open
            onClose={onClose}
            title={`Editar · ${product.sku}`}
            footer={
                <EditorFooter
                    formId={formId}
                    submitLabel="Guardar cambios"
                    isPending={update.isPending}
                    error={error}
                    onCancel={onClose}
                />
            }
        >
            <form
                id={formId}
                noValidate
                autoComplete="off"
                aria-label={`Editar ${product.sku}`}
                className="flex flex-col gap-5"
                onSubmit={(event) => {
                    void form.handleSubmit(async (values) => {
                        const full = toUpdateProductRequest(values, product);
                        const patch = integrated ? toIntegratedProductPatch(full) : full;
                        if (Object.keys(patch).length === 0) {
                            onClose();
                            return;
                        }
                        setError(null);
                        try {
                            await attempt.run(patch, (key) =>
                                update.mutateAsync({ body: patch, key }),
                            );
                            toast.success('Cambios guardados', { description: product.sku });
                            onClose();
                        } catch (failure) {
                            setError(applyWriteError(failure, attempt, patch).message);
                        }
                    })(event);
                }}
            >
                {integrated ? (
                    <IntegratedProductFields
                        control={shared}
                        leading={<IntegrationLockNotice message="lockedProduct" />}
                    />
                ) : null}
                {kind === 'PRODUCT' && !integrated ? (
                    <ProductFields
                        control={shared}
                        allowNoTier={false}
                        leading={
                            <p className="text-[11px] text-muted-foreground">
                                Rastreo por <strong>{trackingLabel(product.trackingMode)}</strong>:
                                no se puede cambiar. {KEEP_HINT}
                            </p>
                        }
                    />
                ) : null}
                {kind === 'KIT' ? (
                    <KitFields form={form} product={product} retailerId={retailerId} />
                ) : null}
                {kind === 'SERVICE' ? <ServiceFields control={shared} /> : null}
                {kind === 'PRODUCT' ? null : (
                    <p className="text-[10.5px] text-muted-foreground">{KEEP_HINT}</p>
                )}
            </form>
            <PriceSection sku={product.sku} locked={integrated} />
        </EditorDrawer>
    );
}

/** Se monta al abrir y por SKU: cambiar de producto arranca el formulario del producto nuevo. */
export function EditItemDrawer({
    product,
    retailerId,
    isOpen,
    onClose,
}: {
    product: CatalogProduct;
    retailerId: string | null;
    isOpen: boolean;
    onClose: () => void;
}) {
    if (!isOpen) return null;
    return (
        <ItemEditor key={product.sku} product={product} retailerId={retailerId} onClose={onClose} />
    );
}
